import { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';

/** MTM wordmark — Barlow Condensed, the brand display face. */
export function Wordmark({ className = '' }: { className?: string }) {
  return (
    <div className={`h-display text-xl leading-none ${className}`}>
      <span className="text-soft">MTM</span>
      <span className="text-power"> OUTREACH</span>
    </div>
  );
}

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'ghost' | 'danger';
  loading?: boolean;
};
export function Button({ variant = 'primary', loading, children, className = '', disabled, ...rest }: BtnProps) {
  const cls = variant === 'primary' ? 'btn-primary' : variant === 'danger' ? 'btn-danger' : 'btn-ghost';
  return (
    <button className={`${cls} ${className}`} disabled={disabled || loading} {...rest}>
      {loading && <Spinner className="h-4 w-4" />}
      {children}
    </button>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block font-head text-sm font-semibold text-soft/90">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-muted">{hint}</span>}
    </label>
  );
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className="input" {...props} />;
}

export function Spinner({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
      <path className="opacity-90" d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

/** Full-panel loading state. */
export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-20 text-muted">
      <Spinner />
      <span className="text-sm">{label}</span>
    </div>
  );
}

/** Real empty state (Task 1) — never show a blank table. */
export function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon?: ReactNode;
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className="card flex flex-col items-center gap-3 px-6 py-14 text-center">
      {icon && <div className="text-electric">{icon}</div>}
      <h3 className="font-head text-lg font-semibold text-soft">{title}</h3>
      {body && <p className="max-w-sm text-sm text-muted">{body}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function Alert({ kind = 'error', children }: { kind?: 'error' | 'info' | 'success'; children: ReactNode }) {
  const map = {
    error: 'border-danger/40 bg-danger/10 text-danger',
    info: 'border-electric/40 bg-electric/10 text-electric',
    success: 'border-success/40 bg-success/10 text-success',
  } as const;
  return <div className={`rounded-xl border px-3.5 py-2.5 text-sm ${map[kind]}`}>{children}</div>;
}

export function RoleBadge({ role }: { role: string }) {
  const map: Record<string, string> = {
    owner: 'border-victory/40 bg-victory/10 text-victory',
    manager: 'border-electric/40 bg-electric/10 text-electric',
    member: 'border-line-strong bg-surface-3 text-muted',
  };
  return (
    <span className={`rounded-md border px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${map[role] || map.member}`}>
      {role}
    </span>
  );
}

export function Modal({
  open, onClose, title, children,
}: { open: boolean; onClose: () => void; title: string; children: ReactNode }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="card mt-[6vh] w-full max-w-lg p-5 shadow-pop" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="h-display text-xl text-soft">{title}</h3>
          <button onClick={onClose} className="rounded-lg px-2 text-muted hover:text-soft" aria-label="Close">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Badge({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <span className={`rounded-md border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${className}`}>
      {children}
    </span>
  );
}

export function Avatar({ name, email }: { name?: string | null; email: string }) {
  const initials = (name || email)
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('');
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-power to-electric text-sm font-semibold text-white">
      {initials || '?'}
    </div>
  );
}
